import axios, { AxiosError, AxiosRequestConfig } from 'axios';
import { ref } from 'vue';
import router from '@/router';
import Cookies from 'js-cookie';
import { STORAGE_KEYS } from '@/constants/StorageKeys';

export const axiosClient = axios.create({
    headers: {
        'Content-Type': 'application/json',
    },
});

axiosClient.interceptors.request.use(
    (config) => {
        const token = Cookies.get(STORAGE_KEYS.TOKEN) ?? process.env.VUE_APP_AUTH_TOKEN;
        const organizationId = Cookies.get(STORAGE_KEYS.ORGANIZATION)
            ? JSON.parse(Cookies.get(STORAGE_KEYS.ORGANIZATION) ?? 'null')?.id
            : process.env.VUE_APP_ORGANIZATION_ID;

        if (token) {
            config.headers['Authorization'] = `Bearer ${token}`;
        }

        if (organizationId) {
            config.headers['X-Organization-Id'] = organizationId;
        }

        return config;
    },
    (error: AxiosError) => {
        return Promise.reject(error);
    },
);

axiosClient.interceptors.response.use(
    (response) => response,
    (error) => {
        const token = Cookies.get(STORAGE_KEYS.TOKEN) ?? process.env.VUE_APP_AUTH_TOKEN;
        const organizationId = Cookies.get(STORAGE_KEYS.ORGANIZATION)
            ? JSON.parse(Cookies.get(STORAGE_KEYS.ORGANIZATION) ?? 'null')?.id
            : process.env.VUE_APP_ORGANIZATION_ID;

        const currentPath = window.location.pathname;
        const isPublicPath =
            ['/login', '/password-recovery', '/reset-password', '/register', '/terms-of-use', '/v2/login'].includes(
                currentPath,
            ) ||
            currentPath.startsWith('/checkout/video/') ||
            currentPath.startsWith('/checkout/success/') ||
            currentPath.startsWith('/purchases/') ||
            currentPath.match(/\/v2\/[0-9a-fA-F-]+/);

        if (error.response && error.response.status === 401 && !isPublicPath) {
            router.push({ name: 'login' });
            if (token) Cookies.remove(STORAGE_KEYS.TOKEN);
            if (organizationId) Cookies.remove(STORAGE_KEYS.ORGANIZATION);
        }
        if (error.response.data && error.response.data.errors) {
            // error.response.data.errors.forEach((error: ApiError) => {
            //   if ((ApiUniqueCodes[error.uniqueCode] || error.message) && (ApiUniqueCodes[error.uniqueCode] != '500')) {
            //     toast({
            //       title: ApiUniqueCodes[error.uniqueCode] || error.message,
            //       variant: "destructive",
            //       duration: 2500
            //     })
            //   }
            // })
        }
        return Promise.reject(error);
    },
);

export interface ApiErrorResponse {
    errors: {
        [key: string]: string[]; // As chaves são os nomes dos campos, e os valores são arrays de mensagens de erro
    };
    message: string;
}

export const isPerformingRequest = ref<boolean>(false);

export function useAxios(baseUrl: string = process.env.VUE_APP_API_BASE_URL || '') {
    const get = async (url: string, config: AxiosRequestConfig = {}) => {
        isPerformingRequest.value = true;

        try {
            return await axiosClient.get(url, {
                ...config,
                baseURL: baseUrl,
            });
        } catch (error) {
            if (axios.isAxiosError(error)) {
                // const e = error as AxiosError<ApiErrorResponse>
                // for (const input in e.response?.data.errors) {
                //   // toast.error(e.response?.data.errors[input][0]!)
                // }
                console.error('Erro na requisição:', error);
            } else {
                console.error('Erro na requisição:', error);
            }
            throw error; // Lança o erro novamente para que o componente que chama useAxios possa lidar com ele.
        } finally {
            isPerformingRequest.value = false;
        }
    };

    /* eslint-disable @typescript-eslint/no-explicit-any */
    const post = async (url: string, body: any = {}, config: AxiosRequestConfig = {}) => {
        isPerformingRequest.value = true;
        try {
            const headers: any = { ...config.headers };
            if (body instanceof FormData) {
                headers['Content-Type'] = null;
            }

            return await axiosClient.post(url, body, {
                baseURL: baseUrl,
                ...config,
                headers,
            });
        } catch (error) {
            if (axios.isAxiosError(error)) {
                // const e = error as AxiosError<ApiErrorResponse>
                // for (const input in e.response?.data.errors) {
                //   // toast.error(e.response?.data.errors[input][0]!)
                // }
                console.error('Erro na requisição:', error);
            } else {
                console.error('Erro na requisição:', error);
            }
            throw error; // Lança o erro novamente para que o componente que chama useAxios possa lidar com ele.
        } finally {
            isPerformingRequest.value = false;
        }
    };

    const patch = async (url: string, body?: FormData | Record<string, any>, config: AxiosRequestConfig = {}) => {
        isPerformingRequest.value = true;
        try {
            const headers: any = { ...config.headers };
            if (body instanceof FormData) {
                headers['Content-Type'] = null;
            }

            return await axiosClient.patch(url, body, {
                ...config,
                headers,
                baseURL: baseUrl,
            });
        } catch (error) {
            if (axios.isAxiosError(error)) {
                // const e = error as AxiosError<ApiErrorResponse>
                console.error('Erro na requisição:', error);
            } else {
                console.error('Erro na requisição:', error);
            }
            throw error;
        } finally {
            isPerformingRequest.value = false;
        }
    };

    const put = async (url: string, body: FormData | Record<string, any>, config: AxiosRequestConfig = {}) => {
        isPerformingRequest.value = true;
        try {
            const headers: any = { ...config.headers };
            if (body instanceof FormData) {
                headers['Content-Type'] = null;
            }

            return await axiosClient.put(url, body, {
                ...config,
                headers,
                baseURL: baseUrl,
            });
        } catch (error) {
            if (axios.isAxiosError(error)) {
                // const e = error as AxiosError<ApiErrorResponse>
                console.error('Erro na requisição:', error);
            } else {
                console.error('Erro na requisição:', error);
            }
            throw error;
        } finally {
            isPerformingRequest.value = false;
        }
    };
    /* eslint-enable @typescript-eslint/no-explicit-any */

    return {
        get,
        post,
        patch,
        put,
        delete: async (url: string, params?: Record<string, unknown>) => {
            isPerformingRequest.value = true;
            try {
                return await axiosClient.delete(url, { params: params, baseURL: baseUrl });
            } catch (error) {
                if (axios.isAxiosError(error)) {
                    // const e = error as AxiosError<ApiErrorResponse>
                    console.error('Erro na requisição:', error);
                } else {
                    console.error('Erro na requisição:', error);
                }
                throw error;
            } finally {
                isPerformingRequest.value = false;
            }
        },
    };
}
